<template>
  <div class="flex flex-col min-h-screen bg-slate-50">
    <Header />
    <FlashMessage v-if="messages.text !== ''" :message="messages" />

    <div class="flex-1 py-7">
      <div class="mx-auto max-w-[1100px] px-4 sm:px-6">
        <!-- Page Header -->
        <div class="flex items-center gap-4 mb-6">
          <router-link
            :to="{ name: 'SurveyList' }"
            class="p-2 rounded-xl text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </router-link>
          <div>
            <h1 class="text-xl font-extrabold text-gray-800">
              {{ isEdit ? "Edit Survey" : "Create New Survey" }}
            </h1>
            <p class="text-xs text-gray-400 mt-0.5">
              {{
                isEdit
                  ? "Update your survey details and questions"
                  : "Build a new survey with custom questions"
              }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- LEFT: Survey Meta + Questions -->
          <div class="lg:col-span-2 flex flex-col gap-5">
            <!-- Survey Info Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
            >
              <h2 class="text-sm font-semibold text-slate-700 mb-4">
                Survey Information
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <!-- Survey Title -->
                <div class="md:col-span-2">
                  <label class="block text-xs font-medium text-gray-600 mb-1">
                    Survey Title <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    placeholder="Enter survey title"
                    class="w-full h-10 px-3 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <!-- Description -->
                <div class="md:col-span-2">
                  <label class="block text-xs font-medium text-gray-600 mb-1">
                    Description
                  </label>
                  <textarea
                    v-model="form.description"
                    rows="2"
                    placeholder="Enter survey description"
                    class="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>
                <div class="space-y-4">
               
                  <!-- Date Range -->
                  <div
                   
                    class="grid grid-cols-2 gap-3"
                  >
                    <div>
                      <label
                        class="block text-[11px] font-medium text-gray-600 mb-1"
                      >
                        Start
                      </label>

                      <input
                        type="date"
                        v-model="form.start_date"
                        class="w-full h-9 px-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                    </div>

                    <div>
                      <label
                        class="block text-[11px] font-medium text-gray-600 mb-1"
                      >
                        End
                      </label>

                      <input
                        type="date"
                        v-model="form.end_date"
                        class="w-full h-9 px-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  <!-- Publish -->
                  <div
                    class="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <div>
                      <p class="text-xs font-medium text-slate-700">
                        Publish Survey
                      </p>
                      <p class="text-[10px] text-slate-500">
                        Visible to students
                      </p>
                    </div>

                    <label class="relative inline-flex cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="form.is_published"
                        class="sr-only peer"
                      />

                      <div
                        class="w-9 h-5 rounded-full bg-gray-300 transition peer-checked:bg-emerald-500 after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
                      ></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Questions Builder -->
            <div
              class="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
            >
              <div class="flex items-center justify-between mb-4">
                <h2
                  class="text-sm font-bold text-slate-700 flex items-center gap-2"
                >
                  Questions
                  <span
                    class="ml-1 bg-blue-50 text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full"
                  >
                    {{ questions.length }}
                  </span>
                </h2>
                <button
                  @click="showTypePicker = true"
                  class="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition-all shadow-sm"
                >
                  <svg
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Question
                </button>
              </div>

              <!-- Empty State -->
              <div
                v-if="questions.length === 0"
                class="py-14 flex flex-col items-center gap-3 border-2 border-dashed border-slate-200 rounded-2xl"
              >
                <div
                  class="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center"
                >
                  <svg
                    class="w-7 h-7 text-slate-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p class="text-slate-500 font-semibold text-sm">
                  No questions yet
                </p>
                <p class="text-slate-400 text-xs text-center max-w-[220px]">
                  Click "Add Question" to start building your survey from 19
                  question types.
                </p>
              </div>

              <!-- Question Cards -->
              <div v-else class="space-y-3" ref="questionsContainer">
                <div
                  v-for="(q, idx) in questions"
                  :key="q.uid || idx"
                  :class="[
                    'group border rounded-2xl p-4 transition-all',
                    q.is_published !== false
                      ? 'border-slate-100 bg-slate-50 hover:border-blue-200 hover:bg-blue-50/30'
                      : 'border-dashed border-slate-200 bg-slate-100/40 opacity-70 hover:opacity-90',
                  ]"
                >
                  <!-- Q Header -->
                  <div class="flex flex-col gap-2 mb-4">
                    <div class="flex items-center gap-2 w-full">
                      <span
                        class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-[11px] font-black rounded-lg flex items-center justify-center"
                      >
                        {{ idx + 1 }}
                      </span>

                      <div class="flex-1 min-w-0">
                        <input
                          v-model="q.question_text"
                          type="text"
                          :placeholder="'Enter question ' + (idx + 1) + '...'"
                          class="w-full bg-transparent border-b border-slate-200 focus:border-blue-500 text-[12px] font-semibold text-slate-800 py-1 focus:outline-none transition-colors placeholder-slate-400"
                        />
                      </div>
                    </div>

                    <div
                      class="flex items-center justify-between pl-8 w-full flex-nowrap"
                    >
                      <div class="flex items-center gap-2 flex-wrap">
                        <span
                          class="inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700 text-[10px] font-bold px-3 py-1 rounded-full border border-indigo-200"
                        >
                          <span
                            class="w-1.5 h-1.5 bg-indigo-500 rounded-full"
                          ></span>
                          {{ getQuestionTypeName(q.question_type) }}
                        </span>
                      </div>

                      <!-- Right: Action Icons -->
                      <div class="flex items-center gap-1.5 flex-shrink-0">
                        <!-- Published / Draft Toggle -->
                        <button
                          @click="q.is_published = !q.is_published"
                          :title="
                            q.is_published
                              ? 'Question is Published (Active)'
                              : 'Question is Unpublished (Draft)'
                          "
                          :class="
                            q.is_published
                              ? 'text-green-500 hover:text-green-600 bg-green-50'
                              : 'text-slate-400 hover:text-slate-500 bg-slate-100'
                          "
                          class="p-1.5 rounded-md transition-colors"
                        >
                          <svg
                            v-if="q.is_published"
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          <svg
                            v-else
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"
                            />
                          </svg>
                        </button>

                        <!-- Required Toggle -->
                        <button
                          @click="q.required = !q.required"
                          :title="q.required ? 'Required' : 'Optional'"
                          :class="
                            q.required
                              ? 'text-red-500 bg-red-50 hover:bg-red-100'
                              : 'text-slate-400 hover:text-slate-500 bg-slate-100'
                          "
                          class="p-1.5 rounded-md transition-all"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                            />
                          </svg>
                        </button>

                        <div class="h-4 w-[1px] bg-slate-200 mx-0.5"></div>

                        <!-- Move Up -->
                        <button
                          @click="moveQuestion(idx, -1)"
                          :disabled="idx === 0"
                          :class="
                            idx === 0
                              ? 'opacity-25 pointer-events-none'
                              : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'
                          "
                          class="p-1.5 rounded-md transition-colors"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2.5"
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        </button>

                        <!-- Move Down -->
                        <button
                          @click="moveQuestion(idx, 1)"
                          :disabled="idx === questions.length - 1"
                          :class="
                            idx === questions.length - 1
                              ? 'opacity-25 pointer-events-none'
                              : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'
                          "
                          class="p-1.5 rounded-md transition-colors"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2.5"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        <!-- Delete -->
                        <button
                          @click="removeQuestion(idx)"
                          class="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors ml-1"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Single Choice (type 2) -->
                  <div
                    v-if="
                      ['radio', 'checkbox', 'dropdown'].includes(
                        getQuestionTitle(q.question_type),
                      )
                    "
                  >
                    <div class="space-y-1.5 mb-2">
                      <div
                        v-for="(opt, oi) in q.config.options"
                        :key="oi"
                        class="flex items-center gap-2"
                      >
                        <span class="w-4 flex-shrink-0">
                          <span
                            v-if="q.question_type === 2"
                            class="w-3.5 h-3.5 rounded-full border-2 border-slate-300 block"
                          ></span>
                          <span
                            v-else-if="q.question_type === 3"
                            class="w-3.5 h-3.5 rounded border-2 border-slate-300 block"
                          ></span>
                          <span v-else class="text-xs text-slate-400"
                            >{{ oi + 1 }}.</span
                          >
                        </span>
                        <input
                          v-model="q.config.options[oi]"
                          type="text"
                          :placeholder="'Option ' + (oi + 1)"
                          class="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        />
                        <button
                          @click="q.config.options.splice(oi, 1)"
                          class="text-gray-300 hover:text-red-400 transition-all"
                        >
                          <svg
                            class="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button
                      @click="q.config.options.push('')"
                      class="text-xs text-blue-600 hover:underline font-semibold flex items-center gap-1"
                    >
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Option
                    </button>
                  </div>

                  <!-- Rating Scale (type 5) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'rating'"
                    class="flex flex-wrap items-end gap-4"
                  >
                    <div>
                      <label class="block text-xs text-gray-500 mb-1"
                        >Min</label
                      >
                      <input
                        v-model.number="q.config.min"
                        type="number"
                        min="1"
                        class="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-center"
                      />
                    </div>

                    <div>
                      <label class="block text-xs text-gray-500 mb-1"
                        >Max</label
                      >
                      <input
                        v-model.number="q.config.max"
                        type="number"
                        :min="q.config.min"
                        max="20"
                        class="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-center"
                      />
                    </div>

                    <div class="flex flex-wrap gap-2 flex-1">
                      <span
                        v-for="n in q.config.max"
                        :key="n"
                        class="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold"
                      >
                        {{ n }}
                      </span>
                    </div>
                  </div>

                  <!-- Likert Scale (type 6) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'likert'"
                  >
                    <label
                      class="block text-[11px] font-medium text-slate-500 mb-2"
                    >
                      Likert Scale
                    </label>

                    <div class="space-y-2">
                      <div
                        v-for="(option, index) in q.config.options"
                        :key="index"
                        class="flex items-center gap-2"
                      >
                        <input
                          v-model="q.config.options[index]"
                          type="text"
                          :placeholder="'Option ' + (index + 1)"
                          class="flex-1 h-8 px-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />

                        <button
                          @click="q.config.options.splice(index, 1)"
                          class="text-gray-300 hover:text-red-400 transition-all"
                        >
                          <svg
                            class="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      @click="q.config.options.push('')"
                      class="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      + Add Option
                    </button>

                    <div class="mt-3">
                      <p class="text-xs text-slate-500 mb-2">Preview</p>

                      <div class="flex flex-wrap gap-2">
                        <span
                          v-for="(option, index) in q.config.options"
                          :key="'preview-' + index"
                          class="px-2 py-1 text-xs rounded-md bg-blue-100 text-blue-700"
                        >
                          {{ option || `Option ${index + 1}` }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Star Rating (type 7) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'star'"
                    class="flex items-center gap-3"
                  >
                    <label class="text-xs text-gray-500">Stars:</label>
                    <select
                      v-model.number="q.config.max_stars"
                      class="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option v-for="n in [3, 4, 5, 7, 10]" :key="n" :value="n">
                        {{ n }}
                      </option>
                    </select>
                    <div class="flex gap-0.5">
                      <svg
                        v-for="s in q.config.max_stars"
                        :key="s"
                        class="w-5 h-5 text-amber-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>

                  <!-- Yes/No (type 8) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'yes no'"
                    class="flex gap-3"
                  >
                    <span
                      class="bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-4 py-1.5 rounded-xl"
                      >Yes</span
                    >
                    <span
                      class="bg-red-50 border border-red-200 text-red-600 text-xs font-semibold px-4 py-1.5 rounded-xl"
                      >No</span
                    >
                  </div>

                  <!-- True/False (type 9) -->
                  <div
                    v-else-if="
                      getQuestionTitle(q.question_type) === 'true false'
                    "
                    class="flex gap-3"
                  >
                    <label
                      class="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg cursor-pointer"
                    >
                      <input type="radio" disabled class="text-blue-600" />
                      <span class="text-sm text-slate-700">True</span>
                    </label>

                    <label
                      class="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg cursor-pointer"
                    >
                      <input type="radio" disabled class="text-blue-600" />
                      <span class="text-sm text-slate-700">False</span>
                    </label>
                  </div>
                  <!-- Short Text (type 10) -->
                  <div
                    v-else-if="
                      getQuestionTitle(q.question_type) === 'short text'
                    "
                  >
                    <input
                      type="text"
                      readonly
                      placeholder="Short answer placeholder..."
                      class="w-full border readonly border-dashed border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </div>

                  <!-- Long Text (type 11) -->
                  <div
                    v-else-if="
                      getQuestionTitle(q.question_type) === 'long text'
                    "
                  >
                    <textarea
                      rows="2"
                      readonly
                      placeholder="Paragraph answer placeholder..."
                      class="w-full border border-dashed border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white resize-none"
                    ></textarea>
                  </div>

                  <!-- Numeric Input (type 12) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'number'"
                    class="flex items-center gap-4"
                  >
                    <div>
                      <label class="text-xs text-gray-500 block mb-1"
                        >Min Value</label
                      >
                      <input
                        v-model.number="q.config.min"
                        type="number"
                        class="w-20 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label class="text-xs text-gray-500 block mb-1"
                        >Max Value</label
                      >
                      <input
                        v-model.number="q.config.max"
                        type="number"
                        class="w-20 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  <!-- Date (type 13) -->
                  <div
                    v-else-if="
                      getQuestionTitle(q.question_type) === 'date time'
                    "
                    class="flex gap-3"
                  >
                    <div class="flex-1">
                      <label class="text-xs text-gray-500 block mb-1"
                        >Date</label
                      >
                      <input
                        type="date"
                        class="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div class="flex-1">
                      <label class="text-xs text-gray-500 block mb-1"
                        >Time</label
                      >
                      <input
                        type="time"
                        class="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  <!-- Phone (type 14) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'phone'"
                  >
                    <input
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      readonly
                      class="border border-dashed border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-400 focus:outline-none w-56 bg-white"
                    />
                    <p class="text-[11px] text-slate-400 mt-1">
                      Phone number with country code validation
                    </p>
                  </div>

                  <!-- URL (type 15) -->
                  <div v-else-if="getQuestionTitle(q.question_type) === 'url'">
                    <input
                      type="url"
                      readonly
                      placeholder="https://example.com"
                      class="border border-dashed border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-400 focus:outline-none w-72 bg-white"
                    />
                    <p class="text-[11px] text-slate-400 mt-1">
                      Validates URL format on submission
                    </p>
                  </div>

                  <!-- Matrix/Grid (type 16) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'matrix'"
                  >
                    <div class="flex gap-4 mb-2">
                      <div class="flex-1">
                        <label class="text-xs text-gray-500 mb-1 block"
                          >Rows (items)</label
                        >
                        <div class="space-y-1">
                          <div
                            v-for="(row, ri) in q.config.rows || []"
                            :key="ri"
                            class="flex gap-2 items-center"
                          >
                            <input
                              v-model="q.config.rows[ri]"
                              type="text"
                              :placeholder="'Row ' + (ri + 1)"
                              class="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <button
                              @click="q.config.rows.splice(ri, 1)"
                              class="text-red-300 hover:text-red-500"
                            >
                              <svg
                                class="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                          <button
                            @click="(q.config.rows || []).push('')"
                            class="text-xs text-blue-600 hover:underline font-medium"
                          >
                            + Add Row
                          </button>
                        </div>
                      </div>
                      <div class="flex-1">
                        <label class="text-xs text-gray-500 mb-1 block"
                          >Columns</label
                        >
                        <div class="space-y-1">
                          <div
                            v-for="(col, ci) in q.config.columns || []"
                            :key="ci"
                            class="flex gap-2 items-center"
                          >
                            <input
                              v-model="q.config.columns[ci]"
                              type="text"
                              :placeholder="'Col ' + (ci + 1)"
                              class="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <button
                              @click="q.config.columns.splice(ci, 1)"
                              class="text-red-300 hover:text-red-500"
                            >
                              <svg
                                class="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                          <button
                            @click="(q.config.columns || []).push('')"
                            class="text-xs text-blue-600 hover:underline font-medium"
                          >
                            + Add Column
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Slider (type 17) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'slider'"
                    class="flex items-center gap-4 flex-wrap"
                  >
                    <div>
                      <label class="text-xs text-gray-500 block mb-1"
                        >Min</label
                      >
                      <input
                        v-model.number="q.config.min"
                        type="number"
                        class="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label class="text-xs text-gray-500 block mb-1"
                        >Max</label
                      >
                      <input
                        v-model.number="q.config.max"
                        type="number"
                        class="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label class="text-xs text-gray-500 block mb-1"
                        >Step</label
                      >
                      <input
                        v-model.number="q.config.step"
                        type="number"
                        min="1"
                        class="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                    <div class="flex-1 pt-4">
                      <input
                        type="range"
                        :min="q.config.min"
                        :max="q.config.max"
                        :step="q.config.step"
                        class="w-full accent-blue-600"
                      />
                    </div>
                  </div>

                  <!-- Image Choice (type 18) -->
                  <div
                    v-else-if="
                      getQuestionTitle(q.question_type) === 'image choice'
                    "
                  >
                    <div class="flex flex-wrap gap-3">
                      <!-- Image Options -->
                      <div
                        v-for="(img, ii) in q.config.options || []"
                        :key="ii"
                        class="relative w-28"
                      >
                        <div
                          class="w-28 h-28 border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-white cursor-pointer hover:border-blue-400"
                          @click="editImage(q, ii)"
                        >
                          <img
                            v-if="img.image"
                            :src="img.image"
                            class="w-full h-full object-cover"
                          />

                          <div
                            v-else
                            class="w-full h-full flex flex-col items-center justify-center"
                          >
                            <svg
                              class="w-8 h-8 text-slate-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.5"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
                              />
                            </svg>

                            <span class="text-[10px] text-slate-400 mt-1">
                              No Image
                            </span>
                          </div>
                        </div>

                        <input
                          v-model="img.label"
                          class="mt-2 w-full text-xs border rounded px-2 py-1"
                          placeholder="Label"
                        />

                        <button
                          @click="removeImageOption(q, ii)"
                          class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white"
                        >
                          ×
                        </button>
                      </div>

                      <!-- Add Image -->
                      <button
                        @click="openImagePicker(q)"
                        class="w-28 h-28 border-2 border-dashed border-blue-300 rounded-xl flex flex-col items-center justify-center hover:bg-blue-50"
                      >
                        <svg
                          class="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>

                        <span class="text-xs mt-2"> Add Image </span>
                      </button>
                    </div>

                    <!-- Hidden File Input -->
                    <input
                      type="file"
                      accept="image/*"
                      :ref="'imageInput' + q.uid"
                      class="hidden"
                      @change="handleImageUpload($event, q)"
                    />
                  </div>

                  <!-- Consent (type 19) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'consent'"
                  >
                    <div class="flex items-start gap-2">
                      <span
                        class="w-4 h-4 mt-0.5 border-2 border-slate-300 rounded flex-shrink-0"
                      ></span>
                      <textarea
                        v-model="q.config.consent_text"
                        rows="2"
                        
                        placeholder="I agree to the terms and conditions..."
                        class="flex-1 bg-white border border-dashed border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                      ></textarea>
                    </div>
                  </div>

                  <!-- Instructions (type 20) -->
                  <div
                    v-else-if="getQuestionTitle(q.question_type) === 'readonly'"
                  >
                    <textarea
                      v-model="q.config.content"
                      rows="3"
                      
                      placeholder="Enter the information or instructions..."
                      class="w-full bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs text-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                    ></textarea>
                    <p class="text-[11px] text-slate-400 mt-1">
                      This will appear as read-only text — no response required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT: Type Picker + Actions -->
          <div class="flex flex-col gap-5">
            <!-- Actions Card -->
            <div
              class="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sticky top-28"
            >
              <h3
                class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4"
              >
                Actions
              </h3>
              <div class="flex flex-col gap-3">
                <button
                  @click="saveSurvey(true)"
                  :disabled="loading"
                  class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <svg 
                    v-if="loading"
                    class="animate-spin -ml-1 mr-1 h-4 w-4 text-white" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <svg
                    v-else
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {{ loading ? 'Publishing...' : 'Publish Survey' }}
                </button>
                <router-link
                  :to="{ name: 'SurveyList' }"
                  class="w-full border border-slate-200 text-slate-500 hover:bg-slate-50 font-semibold py-2.5 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                >
                  Cancel
                </router-link>
              </div>

              <!-- Stats -->
              <div
                class="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3"
              >
                <div class="bg-slate-50 rounded-xl p-3 text-center">
                  <p class="text-lg font-black text-slate-800">
                    {{ questions.length }}
                  </p>
                  <p class="text-[10px] text-gray-400 font-medium">Questions</p>
                </div>
                <div class="bg-slate-50 rounded-xl p-3 text-center">
                  <p class="text-lg font-black text-red-500">
                    {{ requiredCount }}
                  </p>
                  <p class="text-[10px] text-gray-400 font-medium">Required</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Question Type Picker Modal -->
    <div
      v-if="showTypePicker"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="showTypePicker = false"
    >
      <div
        class="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-xl max-h-[75vh] flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-5 py-4 border-b bg-white"
        >
          <div>
            <h3 class="text-base font-bold text-slate-800">
              Choose Question Type
            </h3>
            <p class="text-xs text-slate-500 mt-1">
              Select one of the 19 question types
            </p>
          </div>
          <button
            @click="showTypePicker = false"
            class="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-500"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="overflow-y-auto p-4 flex-1">
          <div
            v-for="group in questionTypeGroups"
            :key="group.label"
            class="mb-5"
          >
            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="qt in group.types"
                :key="qt.typeId"
                @click="addQuestion(qt.typeId)"
                class="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition"
              >
                <span
                  :class="qt.iconBg"
                  class="w-9 h-9 rounded-lg flex items-center justify-center text-lg"
                >
                  {{ qt.icon }}
                </span>

                <div class="text-left">
                  <p class="text-xs font-semibold text-slate-700">
                    {{ qt.type }}
                  </p>

                  <p class="text-[10px] text-slate-500">
                    {{ qt.desc }}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="border-t px-5 py-3 flex justify-between items-center bg-slate-50"
        >
          <span class="text-xs text-slate-500">
            {{
              questionTypeGroups.reduce(
                (total, group) => total + group.types.length,
                0,
              )
            }}
            Question Types
          </span>

          <button
            @click="showTypePicker = false"
            class="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    
   <div
  v-if="showAlreadyPublishedPopup"
  class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60  p-4"
>
  <div
    class="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-100"
  >
    <!-- Header -->
    <div class="px-8 pt-8 pb-6 text-center">
      <!-- Icon -->
      <div
        class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 ring-4 ring-amber-50"
      >
        <svg
          class="h-8 w-8 text-amber-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18A2 2 0 003.53 21h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
      </div>

      <!-- Title -->
      <h2 class="mt-5 text-2xl font-bold text-slate-900">
        Survey Already Published
      </h2>

      <!-- Description -->
      <p class="mt-4 text-sm leading-6 text-slate-600">
        Only one survey can be published at a time.
      </p>

      <!-- Current Survey -->
      <div
        class="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-amber-700">
          Currently Published
        </p>
        <p class="mt-1 font-semibold text-slate-800">
          {{ publishedSurvey?.title }}
        </p>
      </div>

      <!-- New Survey -->
      <div class="mt-4">
        <p class="text-sm text-slate-600">
          Publish
          <span class="font-semibold text-slate-900">
            "{{ form.title || 'this survey' }}"
          </span>
          instead?
        </p>

        <p class="mt-2 text-xs text-slate-500">
          The current survey will be unpublished automatically.
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex gap-3 border-t border-slate-100 bg-slate-50 px-6 py-5"
    >
      <button
        @click="showAlreadyPublishedPopup = false; loading = false"
        class="flex-1 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700  hover:bg-slate-100"
      >
        Cancel
      </button>

      <button
        @click="confirmSwapPublish"
        class="flex-1 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white  hover:bg-blue-700"
      >
        Unpublish & Publish
      </button>
    </div>
  </div>
</div>

    <!-- Scroll To Top -->
    <button
      v-if="showScrollTop"
      @click="scrollToTop"
      title="Scroll to top"
      class="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center transition-all"
    >
      <svg
        class="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          d="M5 15l7-7 7 7"
        />
      </svg>
    </button>
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import FlashMessage from "@/components/FlashMessage.vue";

export default {
  name: "SurveyCreate",
  data() {
    return {
      loading: false,
      showTypePicker: false,
      isEdit: false,
      surveyId: null,
      form: {
        title: "",
        description: "",
        is_published: false,
        start_date: null,
        end_date: null,
        created_by: 1,
      },
      department_id: "",
      questions: [],
      questionTypeGroups: [],
      selectedQuestion: null,
      selectedImageIndex: null,
      pendingImageUploads: [],
      showAlreadyPublishedPopup: false,
      publishedSurvey: null,
      showScrollTop: false,
      messages: {
        text: "",
        type: "",
      },
    };
  },
  components: {
    FlashMessage,
    Header,
  },
  computed: {
    ...mapGetters(["userInfo"]),
    requiredCount() {
      return this.questions.filter((q) => q.required).length;
    },
  },
  created() {
    this.fetchQuestionType();
    const surveyId = this.$route.params.id;
    if (surveyId) {
      this.isEdit = true;
      this.surveyId = surveyId;
      this.fetchSurveyData(surveyId);
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleWindowScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleWindowScroll);
  },
  methods: {
    handleWindowScroll() {
      this.showScrollTop = window.scrollY > 300;
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async fetchQuestionType() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/question-types/list/?Key=${this.userInfo.token_key}`,
        );

        if (res.data.status === 200) {
          this.questionTypeGroups = [
            {
              label: "Question Types",
              types: res.data.data.map((item) => ({
                typeId: item.id,
                type: this.formatTitle(item.title),
                desc: this.getDescription(item.title),
                icon: this.getIcon(item.title),
                iconBg: this.getIconBg(item.title),
              })),
            },
          ];
        }
      } catch (err) {
        console.error(err);
        this.questionTypeGroups = [];
      }
    },
    formatTitle(title) {
      return title.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },

    getDescription(title) {
      const desc = {
        radio: "Select one option",
        checkbox: "Select multiple options",
        dropdown: "Choose from list",
        rating: "Numeric rating",
        likert: "Agreement scale",
        star: "Star rating",
        yes_no: "Yes / No",
        true_false: "True / False",
        short_text: "Single line input",
        long_text: "Paragraph input",
        number: "Numeric input",
        date_time: "Date & Time",
        phone: "Phone number",
        url: "Website URL",
        matrix: "Matrix/Grid",
        slider: "Range slider",
        image_choice: "Select image",
        consent: "Consent checkbox",
        readonly: "Information only",
      };

      return desc[title] || "";
    },

    getIcon(title) {
      const icons = {
        radio: "🔘",
        checkbox: "☑️",
        dropdown: "⬇️",
        rating: "⭐",
        likert: "📊",
        star: "🌟",
        yes_no: "👍",
        true_false: "✔️",
        short_text: "📝",
        long_text: "📄",
        number: "🔢",
        date_time: "📅",
        phone: "📞",
        url: "🔗",
        matrix: "📋",
        slider: "🎚️",
        image_choice: "🖼️",
        consent: "✅",
        readonly: "ℹ️",
      };

      return icons[title] || "❓";
    },

    getIconBg(title) {
      const colors = {
        radio: "bg-blue-100 text-blue-600",
        checkbox: "bg-green-100 text-green-600",
        dropdown: "bg-yellow-100 text-yellow-600",
        rating: "bg-pink-100 text-pink-600",
        likert: "bg-purple-100 text-purple-600",
        star: "bg-orange-100 text-orange-600",
        yes_no: "bg-indigo-100 text-indigo-600",
        true_false: "bg-red-100 text-red-600",
        short_text: "bg-slate-100 text-slate-700",
        long_text: "bg-slate-100 text-slate-700",
        number: "bg-cyan-100 text-cyan-600",
        date_time: "bg-emerald-100 text-emerald-600",
        phone: "bg-rose-100 text-rose-600",
        url: "bg-teal-100 text-teal-600",
        matrix: "bg-violet-100 text-violet-600",
        slider: "bg-amber-100 text-amber-600",
        image_choice: "bg-fuchsia-100 text-fuchsia-600",
        consent: "bg-lime-100 text-lime-600",
        readonly: "bg-gray-100 text-gray-600",
      };

      return colors[title] || "bg-gray-100 text-gray-600";
    },
    getQuestionTypeName(typeId) {
      const type = this.questionTypeGroups
        .flatMap((group) => group.types)
        .find((item) => item.typeId === typeId);

      return type ? type.type : "Unknown";
    },

    async fetchSurveyData(id) {
      try {
        // Survey Details
        const surveyResponse = await axios.get(
          `${process.env.VUE_APP_API}/survey/${id}/?Key=${this.userInfo.token_key}`,
        );

        const surveyData = surveyResponse.data.data;

        this.form = {
          title: surveyData.title,
          description: surveyData.description,
          is_published: surveyData.is_published,
          start_date: surveyData.start_date
            ? surveyData.start_date.split("T")[0]
            : "",
          end_date: surveyData.end_date
            ? surveyData.end_date.split("T")[0]
            : "",
          created_by: surveyData.created_by,
        };

        // Survey Questions
        const questionResponse = await axios.get(
          `${process.env.VUE_APP_API}/survey-question/list/?Key=${this.userInfo.token_key}&survey__id=${id}`,
        );

        const questions = questionResponse.data.data || [];

        this.questions = questions.map((q) => ({
          id: q.id,
          question_text: q.question_text,

          question_type: Array.isArray(q.question_type)
            ? q.question_type[0]?.id
            : q.question_type?.id || q.question_type,

          required: q.required,
          is_published: q.is_published,
          config: q.config || {},
        }));
      } catch (error) {
        console.error("Error fetching survey:", error);
      }
    },

    addQuestion(typeId) {
      const newQuestion = {
        uid: Date.now() + Math.random(),
        question_text: "",
        question_type: typeId,
        required: false,
        is_published: true,
        config: this.getDefaultConfig(typeId),
      };
      this.questions.push(newQuestion);
      this.showTypePicker = false;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scrollToLastQuestion();
        });
      });
    },

    scrollToLastQuestion() {
      const container = this.$refs.questionsContainer;
      const lastCard = container && container.lastElementChild;

      if (lastCard) {
        lastCard.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },

    getDefaultConfig(typeId) {
      const type = this.questionTypeGroups
        .flatMap((group) => group.types)
        .find((item) => item.typeId === typeId);

      if (!type) return {};

      switch (type.type.toLowerCase()) {
        case "radio":
        case "checkbox":
        case "dropdown":
          return {
            options: ["", ""],
          };

        case "rating":
          return {
            min: 1,
            max: 5,
          };

        case "likert":
          return {
            options: ["", ""], // Only two empty options
          };

        case "star":
          return {
            max_stars: 5,
          };

        case "yes no":
          return {};

        case "true false":
          return {};

        case "short text":
          return {};

        case "long text":
          return {};

        case "number":
          return {
            min: 0,
            max: 100,
          };

        case "date time":
          return {};

        case "phone":
          return {};

        case "url":
          return {};

        case "matrix":
          return {
            rows: ["", ""],
            columns: ["", ""],
          };

        case "slider":
          return {
            min: 0,
            max: 100,
            step: 1,
          };

        case "image choice":
          return {
            options: [],
          };

        case "consent":
          return {
            consent_text: "I agree to the terms and conditions.",
          };

        case "readonly":
          return {
            content: "Please read the instructions carefully.",
          };

        default:
          return {};
      }
    },
    getQuestionTitle(typeId) {
      const item = this.questionTypeGroups
        .flatMap((group) => group.types)
        .find((type) => type.typeId === typeId);

      return item ? item.type.toLowerCase() : "";
    },

    openImagePicker(question) {
      this.selectedQuestion = question;
      this.selectedImageIndex = null;

      const input = this.$refs["imageInput" + question.uid];

      if (Array.isArray(input)) {
        input[0].click();
      } else if (input) {
        input.click();
      }
    },

    editImage(question, index) {
      this.selectedQuestion = question;
      this.selectedImageIndex = index;

      const input = this.$refs["imageInput" + question.uid];

      if (Array.isArray(input)) {
        input[0].click();
      } else if (input) {
        input.click();
      }
    },

    handleImageUpload(event, question) {
      const file = event.target.files[0];

      event.target.value = "";

      if (!file) return;

      if (!question.config.options) {
        question.config.options = [];
      }

      const previewUrl = URL.createObjectURL(file);

      let targetIndex = this.selectedImageIndex;

      if (targetIndex === null || targetIndex === undefined) {
        // Add new option
        targetIndex = question.config.options.length;
        question.config.options.push({
          label: `Option ${question.config.options.length + 1}`,
          image: previewUrl,
        });
      } else {
        // Update existing option
        if (!question.config.options[targetIndex]) {
          question.config.options[targetIndex] = {
            label: `Option ${targetIndex + 1}`,
            image: "",
          };
        }
        question.config.options[targetIndex].image = previewUrl;
      }

      // Hold a direct reference so a later removal that shifts array
      // indexes can't cause this upload to land on the wrong option.
      const option = question.config.options[targetIndex];

      const formData = new FormData();
      formData.append("image", file);

      const uploadPromise = axios
        .post(
          `${process.env.VUE_APP_API}/survey-question/image-upload/?Key=${this.userInfo.token_key}`,
          formData
        )
        .then((response) => {
          const uploadedUrl = response.data?.image;

          if (!uploadedUrl) {
            throw new Error("No image URL returned from upload.");
          }

          if (question.config.options.includes(option)) {
            option.image = uploadedUrl;
          }
        })
        .catch((error) => {
          console.error("Image upload failed:", error);

          this.messages = {
            text: "Image upload failed. Please try again.",
            type: "error",
          };

          if (!question.config.options.includes(option)) return;

          if (option.image === previewUrl) {
            option.image = "";
          }
        })
        .finally(() => {
          URL.revokeObjectURL(previewUrl);

          this.pendingImageUploads = this.pendingImageUploads.filter(
            (p) => p !== uploadPromise
          );
        });

      this.pendingImageUploads.push(uploadPromise);
    },

    removeImageOption(question, index) {
      if (question.config.options) {
        question.config.options.splice(index, 1);
      }
    },

    moveQuestion(index, direction) {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= this.questions.length) return;
      const temp = this.questions[index];
      this.questions[index] = this.questions[newIndex];
      this.questions[newIndex] = temp;
    },

    removeQuestion(index) {
      if (confirm("Are you sure you want to remove this question?")) {
        this.questions.splice(index, 1);
      }
    },

    validateSurvey() {
      // Survey title
      if (!this.form.title?.trim()) {
        alert("Please enter a survey title.");
        return false;
      }

      // At least one question
      // if (!this.questions.length) {
      //   alert("Please add at least one question.");
      //   return false;
      // }

      for (let i = 0; i < this.questions.length; i++) {
        const q = this.questions[i];

        if (!q.question_text?.trim()) {
          alert(`Question ${i + 1}: Please enter question text.`);
          return false;
        }

        const config = q.config || {};

        // Get question type name
        const type = this.questionTypeGroups
          .flatMap((group) => group.types)
          .find((item) => item.typeId === q.question_type);

        if (!type) continue;

        switch (type.type.toLowerCase()) {
          case "radio":
          case "checkbox":
          case "dropdown":
          case "likert": {
            const options = (config.options || []).filter((o) => o && o.trim());

            if (options.length < 2) {
              alert(`Question ${i + 1}: Please add at least 2 options.`);
              return false;
            }
            break;
          }

          case "matrix": {
            const rows = (config.rows || []).filter((r) => r && r.trim());

            const columns = (config.columns || []).filter((c) => c && c.trim());

            if (!rows.length) {
              alert(`Question ${i + 1}: Please add at least one row.`);
              return false;
            }

            if (!columns.length) {
              alert(`Question ${i + 1}: Please add at least one column.`);
              return false;
            }

            break;
          }

          case "image choice": {
            if (!config.options || config.options.length < 2) {
              alert(`Question ${i + 1}: Please add at least 2 images.`);
              return false;
            }

            const validImages = config.options.filter((img) => img.image);

            if (validImages.length < 2) {
              alert(`Question ${i + 1}: Please upload at least 2 images.`);
              return false;
            }

            break;
          }

          case "rating": {
            if (config.min === undefined || config.max === undefined) {
              alert(`Question ${i + 1}: Rating configuration is missing.`);
              return false;
            }

            if (Number(config.min) >= Number(config.max)) {
              alert(`Question ${i + 1}: Rating max must be greater than min.`);
              return false;
            }

            break;
          }

          case "slider": {
            if (config.min === undefined || config.max === undefined) {
              alert(`Question ${i + 1}: Slider configuration is missing.`);
              return false;
            }

            if (Number(config.min) >= Number(config.max)) {
              alert(`Question ${i + 1}: Slider max must be greater than min.`);
              return false;
            }

            break;
          }

          case "number": {
            if (
              config.min !== undefined &&
              config.max !== undefined &&
              Number(config.min) > Number(config.max)
            ) {
              alert(`Question ${i + 1}: Number minimum cannot exceed maximum.`);
              return false;
            }

            break;
          }

          case "star": {
            if (!config.max_stars || config.max_stars < 1) {
              alert(`Question ${i + 1}: Please select the maximum stars.`);
              return false;
            }

            break;
          }

          case "consent": {
            if (!config.consent_text?.trim()) {
              alert(`Question ${i + 1}: Consent text is required.`);
              return false;
            }

            break;
          }

          case "readonly": {
            if (!config.content?.trim()) {
              alert(`Question ${i + 1}: Read-only content is required.`);
              return false;
            }

            break;
          }

          default:
            break;
        }
      }

      return true;
    },

  async saveSurvey() {
    if (!this.validateSurvey()) return;

    if (this.pendingImageUploads.length) {
      this.loading = true;
      await Promise.all(this.pendingImageUploads);
      this.loading = false;
    }

    if (this.form.is_published) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey/list/?Key=${this.userInfo.token_key}&status=Active`
        );
        let surveys = [];
        if (response.data?.data?.status == 200) {
          surveys = response.data.data.data || [];
        } else if (response.data?.status == 200) {
          surveys = response.data.data || [];
        }

        const activeSurvey = surveys.find(
          (item) => item.is_published && item.id != this.surveyId
        );

        if (activeSurvey) {
          this.publishedSurvey = activeSurvey;
          this.showAlreadyPublishedPopup = true;
          this.loading = false;
          return;
        }
      } catch (error) {
        console.error("Error checking published surveys:", error);
      }
      this.loading = false;
    }

    this.executeSaveSurvey();
  },

  async confirmSwapPublish() {
    this.loading = true;
    try {
      const payload = {
        title: this.publishedSurvey.title,
        description: this.publishedSurvey.description,
        is_published: false,
        start_date: this.publishedSurvey.start_date || null,
        end_date: this.publishedSurvey.end_date || null,
      };

      await axios.patch(
        `${process.env.VUE_APP_API}/survey/${this.publishedSurvey.id}/?Key=${this.userInfo.token_key}`,
        payload
      );

      this.showAlreadyPublishedPopup = false;
      await this.executeSaveSurvey();
    } catch (error) {
      console.error("Failed to unpublish old survey", error);
      this.messages = {
        text: "Failed to automatically unpublish the active survey.",
        type: "error",
      };
      this.showAlreadyPublishedPopup = false;
      this.loading = false;
    }
  },

  async executeSaveSurvey() {
  this.loading = true;

  try {
    const surveyData = {
      title: this.form.title,
      description: this.form.description,
      is_published: this.form.is_published,
      start_date: this.form.start_date || null,
      end_date: this.form.end_date || null,
      created_by: this.userInfo.user.id,
    };

    let surveyId = this.surveyId;

    // =====================================
    // CREATE SURVEY
    // =====================================
    if (!this.isEdit) {
      const surveyResponse = await axios.post(
        `${process.env.VUE_APP_API}/survey/create/?Key=${this.userInfo.token_key}`,
        surveyData
      );

      if (
        surveyResponse.status !== 200 &&
        surveyResponse.data?.status !== 200
      ) {
        throw new Error(
          surveyResponse.data?.message || "Survey creation failed."
        );
      }

      surveyId = surveyResponse.data.data.id;
      this.surveyId = surveyId;
    }

    // =====================================
    // UPDATE SURVEY
    // =====================================
    else {
      const surveyResponse = await axios.put(
        `${process.env.VUE_APP_API}/survey/${surveyId}/?Key=${this.userInfo.token_key}`,
        surveyData
      );

      if (
        surveyResponse.status !== 200 &&
        surveyResponse.data?.status !== 200
      ) {
        throw new Error(
          surveyResponse.data?.message || "Survey update failed."
        );
      }
    }

    // =====================================
    // CREATE QUESTIONS
    // =====================================
    if (!this.isEdit) {
      const payload = {
        survey: surveyId,
        questions: this.questions.map((question) => ({
          question_text: question.question_text,
          question_type: question.question_type,
          required: question.required,
          is_published: question.is_published,
          config: question.config || {},
        })),
      };

      const response = await axios.post(
        `${process.env.VUE_APP_API}/survey-question/create/?Key=${this.userInfo.token_key}`,
        payload
      );

      if (
        response.status !== 200 &&
        response.data?.status !== 200
      ) {
        throw new Error(
          response.data?.message || "Failed to create survey questions."
        );
      }
    }

    // =====================================
    // UPDATE QUESTIONS
    // =====================================
    else {
      for (const question of this.questions) {
        const payload = {
          survey: surveyId,
          question_text: question.question_text,
          question_type: question.question_type,
          required: question.required,
          is_published: question.is_published,
          config: question.config || {},
        };

        // Existing Question
        if (question.id) {
          const response = await axios.put(
            `${process.env.VUE_APP_API}/surveys/${surveyId}/questions/${question.id}/?Key=${this.userInfo.token_key}`,
            payload
          );

          if (
            response.status !== 200 &&
            response.data?.status !== 200
          ) {
            throw new Error(
              response.data?.message || "Failed to update question."
            );
          }
        }

        // New Question
        else {
          const response = await axios.post(
            `${process.env.VUE_APP_API}/survey-question/create/?Key=${this.userInfo.token_key}`,
            {
              survey: surveyId,
              questions: [payload],
            }
          );

          if (
            response.status !== 200 &&
            response.data?.status !== 200
          ) {
            throw new Error(
              response.data?.message || "Failed to create question."
            );
          }
        }
      }
    }

    // =====================================
    // SUCCESS
    // =====================================

    this.messages = {
      text: this.isEdit
        ? "Survey updated successfully."
        : "Survey published successfully.",
      type: "success",
    };

    setTimeout(() => {
      this.messages = {
        text: "",
        type: "null",
      };

      this.form = {
        title: "",
        description: "",
        is_published: false,
        start_date: null,
        end_date: null,
        course: "",
        batch: "",
        program: "",
        semester: "",
      };

      this.questions = [];
      this.surveyId = null;

      this.$router.push({
        name: "SurveyList",
      });
    }, 1500);
  } catch (error) {
    console.error(error);
    this.loading = false;

    this.messages = {
      text:
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to save survey.",
      type: "error",
    };
  }
},
  },
};
</script>

<style scoped>
/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Custom range input styling */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
</style>
